// @ts-strict
import { modelOptions, mongoose, PropType, post, pre, prop, type Ref } from "@typegoose/typegoose";
import { type FirstPartyDeviceType } from "../../shared.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { Device } from "../device/device.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { addPositionSyncSaveHook, addPositionSyncUpdateHook, PositionDepthSource } from "../utils/pointPositionSync.js";
import type { Zone } from "../zone/zone.js";

const SCHEMA_KEY = "HistoricalDeviceConfiguration" as const;

type FieldsWithDefaults = "DeviceType" | "deleted";

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "DeviceConfigurationHistory",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<HistoricalDeviceConfiguration>>(...addSchemaKeyHook(SCHEMA_KEY))
@pre<HistoricalDeviceConfiguration>(...addPositionSyncSaveHook(PositionDepthSource.POSITION_DEPTH))
@pre<HistoricalDeviceConfiguration>(...addPositionSyncUpdateHook(PositionDepthSource.POSITION_DEPTH))
export class HistoricalDeviceConfiguration extends SchemaBase<HistoricalDeviceConfiguration, FieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    @prop({ type: () => Boolean, required: false })
    public test?: boolean;

    @prop({ type: () => Number, required: false })
    public latitude?: number;

    @prop({ type: () => Number, required: false })
    public longitude?: number;

    @prop({ type: () => VertexInfo, required: false, _id: false })
    public position?: VertexInfo;

    @prop({ type: () => String, required: true })
    public node_id!: string;

    @prop({ type: () => mongoose.Types.ObjectId, ref: "Organization", required: false })
    public owner?: Ref<Organization>;

    @prop({ type: () => String, required: false })
    public version?: "V1" | "seed" | "sapling";

    @prop({ type: () => String, required: true, default: "spr_2v" })
    public DeviceType: FirstPartyDeviceType = "spr_2v";

    @prop({ type: () => String, required: false })
    public EnclosureID?: string;

    @prop({ type: () => mongoose.Types.ObjectId, ref: "Device", required: false })
    public originalID?: Ref<Device>;

    @prop({ type: () => mongoose.Types.ObjectId, ref: "Zone", required: false }, PropType.ARRAY)
    public connectedZones?: Ref<Zone>[];

    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @prop({ type: () => Date, required: true })
    public date!: Date;

    static getters = {} as const satisfies Record<string, (self: HistoricalDeviceConfiguration) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<HistoricalDeviceConfiguration, FieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}
